import { untrack } from 'svelte';
import type { PlayerView } from '../../shared/protocol';

/** How long a dropped connection stays invisible; ordinary blips reconnect well within it. */
const DISCONNECT_NOTICE_DELAY_MS = 3_000;

/**
 * Presentation-side debounce of the authoritative `connected` flag. The server reports a
 * drop immediately so expiry and host claims stay truthful; screens wait before dimming a
 * player or offering stand-in host controls, so a momentary blip changes nothing on screen.
 */
export class SoftenedPresence {
  private revealed = $state<ReadonlySet<string>>(new Set());
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(private readonly delayMs = DISCONNECT_NOTICE_DELAY_MS) {}

  /** Track the authoritative list; call whenever it changes. */
  observe(players: readonly Readonly<Pick<PlayerView, 'id' | 'connected'>>[]): void {
    // Callers observe from an effect; the effect should depend on the list, not on this state.
    untrack(() => this.reveal(players));
  }

  private reveal(players: readonly Readonly<Pick<PlayerView, 'id' | 'connected'>>[]): void {
    const offline = new Set(players.filter((player) => !player.connected).map((player) => player.id));
    for (const id of offline) {
      if (this.timers.has(id) || this.revealed.has(id)) continue;
      this.timers.set(
        id,
        setTimeout(() => {
          this.timers.delete(id);
          this.revealed = new Set([...this.revealed, id]);
        }, this.delayMs),
      );
    }
    for (const [id, timer] of this.timers) {
      if (offline.has(id)) continue;
      clearTimeout(timer);
      this.timers.delete(id);
    }
    if ([...this.revealed].some((id) => !offline.has(id)))
      this.revealed = new Set([...this.revealed].filter((id) => offline.has(id)));
  }

  /** The list as shown: a connection lost less than the delay ago still reads as connected. */
  soften<T extends Readonly<Pick<PlayerView, 'id' | 'connected'>>>(players: readonly T[]): T[] {
    return players.map((player) =>
      player.connected || this.revealed.has(player.id) ? player : { ...player, connected: true },
    );
  }

  dispose(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
  }
}
